import { UseQueryResult } from '@tanstack/react-query'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { Button, Card, CardBody } from 'react-bootstrap'
import { Bilancia } from '../../types'
import {
  getLabelTipoBilancia,
  getLabelTipoBilanciaEm,
  transformNumberWithCommaAndNumberOfDecimal,
  useIsCliente,
} from '../../utils/utils'

interface CardDatiBilanciaClienteInterface {
  nome_produttore: Bilancia['nome_produttore']
  data_creazione?: Bilancia['data_creazione']
  data_installazione?: Bilancia['data_installazione']
  tipo_bilancia: Bilancia['tipo_bilancia']
  tipo_bilancia_em: Bilancia['tipo_bilancia_em']
  unita_di_misura: Bilancia['unita_di_misura']
  portata_massima: Bilancia['portata_massima']
  portata_massima_secondaria?: Bilancia['portata_massima_secondaria']
  portata_massima_terziaria?: Bilancia['portata_massima_terziaria']
  portata_minima?: Bilancia['portata_minima']
  dimensioni?: Bilancia['dimensioni']
  div_principale?: Bilancia['div_principale']
  div_secondaria?: Bilancia['div_secondaria']
  div_terziaria?: Bilancia['div_terziaria']
}

interface CardDatiBilanciaInterface extends CardDatiBilanciaClienteInterface {
  data_ultima_modifica?: Bilancia['data_ultima_modifica']
  matricole_celle?: Bilancia['matricole_celle']
  pesa_interrata?: Bilancia['pesa_interrata']
  tipo_terminale?: Bilancia['tipo_terminale']
  rif_welmec?: Bilancia['rif_welmec']
  collegamenti_seriali?: Bilancia['collegamenti_seriali']
  classe_di_precisone?: Bilancia['classe_di_precisone']
  lunghezza_cavo?: Bilancia['lunghezza_cavo']
  note?: Bilancia['note']
  dismessa?: Bilancia['dismessa']
  fuori_uso?: Bilancia['fuori_uso']
  sigla?: Bilancia['sigla']
  tipo_celle?: Bilancia['tipo_celle']
  numero_celle?: Bilancia['numero_celle']
  portata_celle?: Bilancia['portata_celle']
  nome_installatore?: Bilancia['nome_installatore']
  data_collaudo?: Bilancia['data_collaudo']
  cert_approv?: Bilancia['cert_approv']
}

type CardDatiBilanciaProps = {
  bilancia: UseQueryResult<CardDatiBilanciaInterface, Error>
  modalActionsForm?: {
    open: (v?: Bilancia | null | undefined) => void
    close: () => void
    toggle: () => void
    onClosed: () => void
  }
}

export default function CardDatiBilancia({ bilancia, modalActionsForm }: CardDatiBilanciaProps) {
  const isCliente = useIsCliente()
  return (
    <Card>
      <CardBody>
        <div className="py-1 border-bottom">
          <div className="d-flex justify-content-between">
            <h4 className="heading-h4">Dati bilancia</h4>
            {!isCliente && (
              <div className="d-flex align-items-center">
                <div className="p-sm">
                  <span className="text-body-secondary">Ultima modifica:</span>{' '}
                  <span>
                    {bilancia.data?.data_ultima_modifica
                      ? dayjs(bilancia.data?.data_ultima_modifica).format('DD.MM.YYYY')
                      : '-'}
                  </span>
                </div>

                <div>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="ms-3 d-flex align-items-center"
                    onClick={() => {
                      modalActionsForm?.open({
                        ...(bilancia.data as Bilancia),
                        matricole_celle_content: bilancia.data?.matricole_celle?.split(/\s+/g) ?? [],
                      })
                    }}
                  >
                    Modifica
                    <span
                      className="material-icons ms-2"
                      style={{
                        fontSize: '16px',
                      }}
                    >
                      edit
                    </span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-md-3">
            <div className="label-m text-body-secondary">Nome produttore</div>
            <div className="p-md mt-2">{bilancia.data?.nome_produttore}</div>
          </div>
          <div className="col-md-3">
            <div className="label-m text-body-secondary">Data creazione</div>
            <div className="p-md mt-2">
              {bilancia.data?.data_creazione ? dayjs(bilancia.data?.data_creazione).format('DD.MM.YYYY') : '-'}
            </div>
          </div>
          <div className="col-md-3">
            <div className="label-m text-body-secondary">Data install.</div>
            <div className="p-md mt-2">
              {bilancia.data?.data_installazione ? dayjs(bilancia.data?.data_installazione).format('DD.MM.YYYY') : '-'}
            </div>
          </div>
          <div className="col-md-3">
            <div className="label-m text-body-secondary">
              Tipo bilancia
              <div className="p-md mt-2">
                {getLabelTipoBilancia(bilancia.data?.tipo_bilancia)} -{' '}
                {getLabelTipoBilanciaEm(bilancia.data?.tipo_bilancia_em)}
              </div>
            </div>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-md-3">
            <div className="label-m text-body-secondary">Portata Massima</div>
            <div className="p-md mt-2">
              {transformNumberWithCommaAndNumberOfDecimal(bilancia.data?.portata_massima, 2) || '-'}{' '}
              {bilancia.data?.unita_di_misura || ''}
            </div>
          </div>
          {bilancia.data?.portata_massima_secondaria && (
            <div className="col-md-3">
              <div className="label-m text-body-secondary">Portata Massima 2</div>
              <div className="p-md mt-2">
                {transformNumberWithCommaAndNumberOfDecimal(bilancia.data?.portata_massima_secondaria, 2) || '-'}{' '}
                {bilancia.data?.unita_di_misura || ''}
              </div>
            </div>
          )}
          {bilancia.data?.portata_massima_terziaria && (
            <div className="col-md-3">
              <div className="label-m text-body-secondary">Portata Massima 3</div>
              <div className="p-md mt-2">
                {transformNumberWithCommaAndNumberOfDecimal(bilancia.data?.portata_massima_terziaria, 2) || '-'}{' '}
                {bilancia.data?.unita_di_misura || ''}
              </div>
            </div>
          )}
          <div className="col-md-3">
            <div className="label-m text-body-secondary">Pesata Minima</div>
            <div className="p-md mt-2">{bilancia.data?.portata_minima || '-'}</div>
          </div>
          <div className="col-md-3">
            <div className="label-m text-body-secondary">Dimensioni</div>
            <div className="p-md mt-2">{bilancia.data?.dimensioni || '-'}</div>
          </div>
          <div
            className={classNames('col-md-3', {
              'mt-3': bilancia.data?.portata_massima_secondaria,
            })}
          >
            <div className="label-m text-body-secondary">Divisione</div>
            <div className="p-md mt-2">
              {bilancia.data?.div_principale || '-'} {bilancia.data?.unita_di_misura || ''}
            </div>
          </div>
          {bilancia.data?.div_secondaria && (
            <div
              className={classNames('col-md-3', {
                'mt-3': bilancia.data?.portata_massima_secondaria,
              })}
            >
              <div className="label-m text-body-secondary">Divisione 2</div>
              <div className="p-md mt-2">
                {bilancia.data?.div_secondaria || '-'} {bilancia.data?.unita_di_misura || ''}
              </div>
            </div>
          )}
          {bilancia.data?.div_terziaria && (
            <div
              className={classNames('col-md-3', {
                'mt-3': bilancia.data?.portata_massima_secondaria,
              })}
            >
              <div className="label-m text-body-secondary">Divisione 3</div>
              <div className="p-md mt-2">
                {bilancia.data?.div_terziaria || '-'} {bilancia.data?.unita_di_misura || ''}
              </div>
            </div>
          )}
        </div>
        {!isCliente && (
          <div>
            <div className="row mt-3">
              <div className="col-md-3">
                <div className="label-m text-body-secondary">Pesa interrata</div>
                <div className="p-md mt-2">{bilancia.data?.pesa_interrata ? 'Si' : 'No'}</div>
              </div>
              <div className="col-md-3">
                <div className="label-m text-body-secondary">Tipo terminale</div>
                <div className="p-md mt-2">
                  {bilancia.data?.tipo_terminale !== '' ? bilancia.data?.tipo_terminale : '-'}
                </div>
              </div>
              <div className="col-md-3">
                <div className="label-m text-body-secondary">Rif. Welmec</div>
                <div className="p-md mt-2">{bilancia.data?.rif_welmec !== '' ? bilancia.data?.rif_welmec : '-'}</div>
              </div>
              <div className="col-md-3">
                <div className="label-m text-body-secondary">Collegamenti ser.</div>
                <div className="p-md mt-2">
                  {bilancia.data?.collegamenti_seriali !== '' ? bilancia.data?.collegamenti_seriali : '-'}
                </div>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-md-3">
                <div className="label-m text-body-secondary">Classe</div>
                <div className="p-md mt-2">
                  {bilancia.data?.classe_di_precisone !== '' ? bilancia.data?.classe_di_precisone : '-'}
                </div>
              </div>
              <div className="col-md-3">
                <div className="label-m text-body-secondary">Lunghezza cavo</div>
                <div className="p-md mt-2">
                  {bilancia.data?.lunghezza_cavo !== '' ? bilancia.data?.lunghezza_cavo : '-'}
                </div>
              </div>
              <div className="col-md-6">
                <div className="label-m text-body-secondary">Note</div>
                <div className="p-md mt-2">{bilancia.data?.note !== '' ? bilancia.data?.note : '-'}</div>
              </div>
              {bilancia.data?.dismessa && (
                <div className="col-md-3 mt-3">
                  <div className="label-m text-body-secondary">Dismessa</div>
                  <div className="p-md mt-2">Si</div>
                </div>
              )}
              {bilancia.data?.fuori_uso && (
                <div className="col-md-3 mt-3">
                  <div className="label-m text-body-secondary">Fuori uso</div>
                  <div className="p-md mt-2">Si</div>
                </div>
              )}
              {bilancia.data?.sigla && (
                <div className="col-md-3 mt-3">
                  <div className="label-m text-body-secondary">Sigla</div>
                  <div className="p-md mt-2">{bilancia.data?.sigla}</div>
                </div>
              )}
            </div>
            <div className="row">
              <div className="col-12 mt-3">
                <div className="border-top" />
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-md-3">
                <div className="label-m text-body-secondary">Tipo cella carico</div>
                <div className="p-md mt-2">{bilancia.data?.tipo_celle !== '' ? bilancia.data?.tipo_celle : '-'}</div>
              </div>
              <div className="col-md-3">
                <div className="label-m text-body-secondary">Numero celle</div>
                <div className="p-md mt-2">{bilancia.data?.numero_celle ? bilancia.data?.numero_celle : '-'}</div>
              </div>
              <div className="col-md-3">
                <div className="label-m text-body-secondary">Portata celle</div>
                <div className="p-md mt-2">{bilancia.data?.portata_celle ? bilancia.data?.portata_celle : '-'}</div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 mt-3">
                <div className="label-m text-body-secondary">Matricole celle</div>
                <div className="p-md mt-2">
                  {bilancia.data?.matricole_celle !== '' ? bilancia.data?.matricole_celle : '-'}
                </div>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-md-4">
                <div className="label-m text-body-secondary">Collaudatore</div>
                <div className="p-md mt-2">
                  {bilancia.data?.nome_installatore !== '' ? bilancia.data?.nome_installatore : '-'}
                </div>
              </div>
              <div className="col-md-4">
                <div className="label-m text-body-secondary">Data collaudo</div>
                <div className="p-md mt-2">
                  {bilancia.data?.data_collaudo ? dayjs(bilancia.data?.data_collaudo).format('DD.MM.YYYY') : '-'}
                </div>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-md-3">
                <div className="label-m text-body-secondary">Provvedimento</div>
                <div className="p-md mt-2">{bilancia.data?.cert_approv !== '' ? bilancia.data?.cert_approv : '-'}</div>
              </div>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
